import { createSelector } from 'reselect'
import { identity } from 'ramda'

import { getRP, selectRPBeingEdited } from 'selectors/referenceParameter'
import { safeToJS, getIn, getInOr } from 'utils/immutable'

export const hydrateListFromDictionary = (dictionary, uriList) => {
  // needs to go in a utils folder
  const uriSet = new Set(uriList)
  return dictionary.reduce(
    (coll, v, k) => (uriSet.has(k) ? [...coll, safeToJS(v)] : coll),
    []
  )
}

const getSupportParameter = (state) => state.get('supportParameter')

const selectSpBeingEditedUri = getInOr('', [
  'supportParameter',
  'spBeingEdited',
  'uri',
])
const selectInvalidSPValues = getInOr(
  [],
  ['supportParameter', 'invalidSPValues']
)

export const getInvalidSPValues = createSelector(
  selectInvalidSPValues,
  identity
)

export const getSPBeingEditedUri = createSelector(
  selectSpBeingEditedUri,
  identity
)

const selectSPBeingEdited = getInOr(null, ['supportParameter', 'spBeingEdited'])

export const getSPBeingEdited = createSelector(selectSPBeingEdited, identity)

const getSupportParameterEditingData = (state) => {
  const supportParameterState = getSupportParameter(state)
  return {
    isEditing: supportParameterState.get('isEditing'),
  }
}

export const getCurrentSupportParameterEditing = createSelector(
  [getSupportParameterEditingData],
  ({ isEditing }) => isEditing
)

export const selectSPDictionary = getIn(['supportParameter', 'dictionary'])

export const getSPsFromUriList = createSelector(
  selectSPDictionary,
  (_, uriList) => uriList,
  hydrateListFromDictionary
)

export const getSPList = ({ spUriList }) =>
  createSelector(selectSPDictionary, (dictionary) =>
    hydrateListFromDictionary(dictionary, spUriList)
  )

const selectFirstLinkedRP = (state, { uri: spUri }) => {
  const arr = safeToJS(
    state.getIn(['supportParameter', 'dictionary', spUri, 'linkedRPs']),
    []
  )
  if (arr.length < 1) return { supportParameters: [] }
  return getRP({ uri: arr[0] })(state)
}

const selectSP = (state, props) =>
  safeToJS(state.getIn(['supportParameter', 'dictionary', props.uri]))

export const getSP = createSelector(
  [selectSPDictionary, selectSP, selectFirstLinkedRP],
  (dictionary, sp, firstLinkedRP) => {
    const siblingSPs =
      (firstLinkedRP &&
      firstLinkedRP.supportParameters && // this check might be able to go away
        hydrateListFromDictionary(
          dictionary,
          firstLinkedRP.supportParameters
        )) ||
      []

    if (!sp) {
      return {
        siblingSPs,
      }
    }

    return {
      currentSP: sp,
      siblingSPs,
    }
  }
)

export const getSPOnly = createSelector(selectSP, identity)

const getCurrentSupportParameterData = (state) => {
  const supportParameterState = getSupportParameter(state)
  return {
    linkedRPs: supportParameterState.get('linkedRPs').toJS(),
    // we'll need an RP to provide semantic context
    isSearching: supportParameterState.get('isSearching'),
    isValidForSaving: supportParameterState.get('descriptionIsValid'),
    invalidSPValues: supportParameterState.get('invalidSPValues'),
    timerIsValid: supportParameterState.get('timerIsValid'),
    isEditing: supportParameterState.get('isEditing'),
    originalSP: supportParameterState.get('originalSP'),
    selectedSlot: supportParameterState.get('selectedSlot'),
    selectedLogicalSlot: supportParameterState.get('selectedLogicalSlot'),
    outputSlotSelected: supportParameterState.get('outputSlotSelected'),
    saveErrors: supportParameterState.get('saveState'),
    inputs: supportParameterState.get('inputs'),
    inputsFetching: supportParameterState.get('inputsFetching'),
    outputs: supportParameterState.get('outputs'),
    outputsFetching: supportParameterState.get('outputsFetching'),
    selectedInput: supportParameterState.get('selectedInput'),
    selectedOutput: supportParameterState.get('selectedOutput'),
    slotEditDisplayMode: supportParameterState.get('slotEditDisplayMode'),
    parameterGroupsFetching: supportParameterState.get(
      'parameterGroupsFetching'
    ),
    parameterGroups: supportParameterState.get('parameterGroups'),
    detailLevel: supportParameterState.get('detailLevel'),
    spEditingDisabled: supportParameterState.get('spEditingDisabled'),
    spBeingEdited: supportParameterState.get('spBeingEdited'),
  }
}

export const getCurrentSupportParameter = createSelector(
  [getCurrentSupportParameterData],
  (supportParameterData) => {
    const inputs = safeToJS(supportParameterData.inputs)

    return {
      spBeingEdited: safeToJS(supportParameterData.spBeingEdited),
      isEditing: supportParameterData.isEditing,
      isValidForSaving: supportParameterData.isValidForSaving,
      invalidSPValues: safeToJS(supportParameterData.invalidSPValues),
      originalSP: safeToJS(supportParameterData.originalSP),
      linkedRPs: supportParameterData.linkedRPs,
      selectedSlot: supportParameterData.selectedSlot,
      selectedLogicalSlot: safeToJS(supportParameterData.selectedLogicalSlot),
      outputSlotSelected: supportParameterData.outputSlotSelected,
      saveErrors: supportParameterData.saveErrors,
      inputs,
      outputs: safeToJS(supportParameterData.outputs),
      inputsFetching: supportParameterData.inputsFetching,
      outputsFetching: supportParameterData.outputsFetching,
      selectedInput: safeToJS(supportParameterData.selectedInput),
      selectedOutput: safeToJS(supportParameterData.selectedOutput),
      slotEditDisplayMode: supportParameterData.slotEditDisplayMode,
      parameterGroupsFetching: supportParameterData.parameterGroupsFetching,
      parameterGroups: supportParameterData.parameterGroups,
      detailLevel: supportParameterData.detailLevel,
      spEditingDisabled: supportParameterData.spEditingDisabled,
    }
  }
)

export const selectSelectedSlot = getIn(['supportParameter', 'selectedSlot'])

export const getSelectedSlot = createSelector(selectSelectedSlot, identity)

export const makeGetSlottedSPProps = () =>
  createSelector(
    selectSelectedSlot,
    selectSpBeingEditedUri,
    selectRPBeingEdited,
    (_, { uri }) => uri,
    (selectedSlot, spBeingEditedUri, rpBeingEdited, myUri) => {
      const isBeingEdited = spBeingEditedUri === myUri
      return {
        selectedSlot,
        isBeingEdited,
        disableAffinities: Boolean(rpBeingEdited) || isBeingEdited, // this should cover editing in RP _or_ solo editing
      }
    }
  )

export const getAppropriateSPData = createSelector(
  selectSPBeingEdited,
  selectSP,
  (spBeingEdited, sp) => {
    if (!spBeingEdited) {
      return sp
    }
    if (sp.uri === spBeingEdited.get('uri')) {
      return spBeingEdited.toJS()
    }
    return sp
  }
)
